import type { Problem } from '@walkkit/shared';
import { s } from '../strings';

export type ApiErrorInit = {
  status: number;
  title: string;
  code?: string | undefined;
  type?: string | undefined;
  detail?: string | undefined;
  issues?: unknown[] | undefined;
  current?: number | undefined;
  max?: number | undefined;
};

type ZodLikeIssue = { path?: (string | number)[]; message?: string };

/** A failed API call, parsed from RFC 9457 problem+json where possible. */
export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly code: string | undefined;
  readonly type: string | undefined;
  readonly detail: string | undefined;
  readonly issues: unknown[];
  readonly current: number | undefined;
  readonly max: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.detail ?? init.title);
    this.name = 'ApiError';
    this.status = init.status;
    this.title = init.title;
    this.code = init.code;
    this.type = init.type;
    this.detail = init.detail;
    this.issues = init.issues ?? [];
    this.current = init.current;
    this.max = init.max;
  }

  /** Status 0 = the request never got a response (offline, DNS, CORS…). */
  static network(cause?: unknown): ApiError {
    const err = new ApiError({ status: 0, title: s.errors.network, code: 'NETWORK' });
    if (cause !== undefined) (err as { cause?: unknown }).cause = cause;
    return err;
  }

  static async fromResponse(res: Response): Promise<ApiError> {
    let body: unknown = null;
    try {
      const text = await res.text();
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    return ApiError.fromBody(res.status, body, res.statusText);
  }

  static fromBody(status: number, body: unknown, statusText = ''): ApiError {
    if (body && typeof body === 'object') {
      const p = body as Partial<Problem> & { error?: unknown; message?: unknown };
      const title =
        (typeof p.title === 'string' && p.title) ||
        (typeof p.message === 'string' && p.message) ||
        (typeof p.error === 'string' && p.error) ||
        statusText ||
        defaultTitle(status);
      return new ApiError({
        status: typeof p.status === 'number' ? p.status : status,
        title,
        code: typeof p.code === 'string' ? p.code : undefined,
        type: typeof p.type === 'string' ? p.type : undefined,
        detail: typeof p.detail === 'string' ? p.detail : undefined,
        issues: Array.isArray(p.issues) ? p.issues : undefined,
        current: typeof p.current === 'number' ? p.current : undefined,
        max: typeof p.max === 'number' ? p.max : undefined,
      });
    }
    return new ApiError({ status, title: statusText || defaultTitle(status) });
  }

  get isNotFound(): boolean {
    return this.status === 404 || this.status === 410;
  }

  get isValidation(): boolean {
    return this.status === 422 || this.status === 400;
  }

  /** Zod issues keyed by dotted path ("email", "fields.spend_cap"); first message wins. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const raw of this.issues) {
      const issue = raw as ZodLikeIssue;
      if (!issue || typeof issue !== 'object') continue;
      const key = Array.isArray(issue.path) && issue.path.length ? issue.path.join('.') : '_form';
      if (!(key in out) && typeof issue.message === 'string') out[key] = issue.message;
    }
    return out;
  }
}

function defaultTitle(status: number): string {
  const e = s.errors;
  if (status === 401) return e.unauthorised;
  if (status === 403) return e.forbidden;
  if (status === 404) return e.notFound;
  if (status === 409) return e.conflict;
  if (status === 422) return e.validation;
  if (status === 429) return e.rateLimited;
  if (status >= 500) return e.server;
  return e.requestFailed;
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Human title for any thrown value. */
export function errorTitle(e: unknown, fallback: string = s.errors.generic): string {
  if (e instanceof ApiError) return e.status === 0 ? s.errors.network : e.title;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
